import React, { useState, useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { MdLocalPhone } from 'react-icons/md';
import { sendAuthenticatedAsyncRequest } from 'services/AsyncRequestService';
import { showNotification } from 'services/NotificationService';
import { VoiceClient } from "@amazon-connect/voice";

const ConnectCallButton = ({ label, body }) => {
  const [loading, setLoading] = useState(false);
  const voiceClient = useMemo(() => new VoiceClient(), []);

  const onAWSConnectClick = useCallback(async () => {
    const sendRequest = () => {
      return new Promise((resolve, reject) => {
        sendAuthenticatedAsyncRequest(
          '/call-center/connect',
          'POST',
          body,
          (response) => resolve(response),
          (error) => reject(error)
        );
      });
    };

    if (!body || !body.phone) {
      showNotification({
        title: 'Error',
        message: 'Invalid request body. Please try again.',
        position: 'tr',
        type: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const responseFromPlatform = await sendRequest();
      if (responseFromPlatform && responseFromPlatform.status === 200) {
        try {
          await voiceClient.createOutboundCall(body.phone);
          showNotification({
            title: 'Action Successful!',
            message: 'Your call is being connected.',
            position: 'tr',
            type: 'success',
          });
        } catch (sdkError) {
          let errorMessage = 'An error occurred while connecting the call. Please try again.';
          if (
            sdkError.errorType === 'ConnectError' &&
            sdkError.errorKey === 'requestHandlerError' &&
            sdkError.details?.command === 'voice/createOutboundCall' &&
            sdkError.details?.errorDetails?.code === 'ERR_BAD_REQUEST'
          ) {
            errorMessage = 'Invalid phone number. Please check the number and try again.';
          }
          showNotification({
            title: 'Call Failed',
            message: errorMessage,
            position: 'tr',
            type: 'error',
          });
        }
      } else {
        showNotification({
          title: 'Call Failed',
          message: 'Unexpected response from the server. Please try again.',
          position: 'tr',
          type: 'error',
        });
      }
    } catch (error) {
      let errorMessage = 'An error occurred while processing your request. Please try again.';
      if (error.response && error.response.status === 401) {
        errorMessage = 'Unauthorized. Please check your permissions.';
      } else if (error.response && error.response.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      showNotification({
        title: 'Call Failed',
        message: errorMessage,
        position: 'tr',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [body, voiceClient]);

  return (
    <Button
      style={{ background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}
      aria-label={label || "Call"}
      onClick={onAWSConnectClick}
      disabled={loading}
    >
      <MdLocalPhone size={18} color={loading ? 'gray' : 'blue'} />
    </Button>
  );
};

export default ConnectCallButton;